import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';

import { PatientGroupsQuery } from '@models/patient-group/patient-groups.query';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionComponent,
    DownloadTemplateButtonComponent,
    FileManagerComponent,
    FileViewerComponent,
    AsyncPipe,
  ],
  selector: 'aux-patient-driver-upload',
  template: `
    <div class="w-screen max-w-[560px]"></div>
    <div class="grid grid-cols-2 gap-5">
      <div class="flex flex-col">
        <div class="">
          <div class="mb-2 text-xs">Patient Group</div>
          <ng-select
            placeholder="Select"
            id="sites"
            [formControl]="selectedPatientGroup"
            [appendTo]="'body'"
            [searchable]="true"
            [clearable]="false"
          >
            <ng-option value="" />
            @for (pt of patientGroupsQuery.selectAll() | async; track pt) {
              <ng-option [value]="pt.id">
                {{ pt.name }}
              </ng-option>
            }
          </ng-select>
        </div>

        <div>
          @if (errorMessage()) {
            <div class="mt-4 rounded-md bg-aux-error p-5 font-medium text-white">
              {{ errorMessage() }}
            </div>
          }
        </div>
      </div>
      <div>
        <aux-download-template-button
          label="Download the template"
          [templateType]="patientDriverTemplateType"
          class="mb-4 flex justify-center"
          [showIcon]="false"
        />

        <aux-file-manager
          class="h-48"
          #manager
          [fetchFilesOnInit]="false"
          [pathFn]="pathFn"
          [eager]="false"
          [metadata]="metadata"
          [showSuccessOnUpload]="true"
        />
      </div>
      <div></div>
      <div class="mt-4 max-h-60 overflow-auto">
        <aux-file-viewer
          [fileManager]="manager"
          [disableFirstFileMargin]="true"
          [onlyShowUploaded]="false"
        />
      </div>
    </div>
  `,
})
export class PatientDriverUploadComponent {
  apiService = inject(ApiService);
  eventService = inject(EventService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  patientGroupsQuery = inject(PatientGroupsQuery);
  ref = inject(CustomOverlayRef);

  errorMessage = signal('');
  loading = signal(false);

  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  metadata: FileMetadata = {};
  patientDriverTemplateType = TemplateType.PATIENT_DRIVER_TEMPLATE;
  selectedPatientGroup = new UntypedFormControl('');

  getFilePath(id: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/sites/${id}/patient-driver/`;
  }

  async onUpload() {
    this.errorMessage.set('');

    if (this.fileManager && !this.loading()) {
      const files = this.fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      this.loading.set(true);

      const file = files[0];
      const key = `${this.getFilePath(this.selectedPatientGroup.value)}${file.bucket_key}`;

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: key,
      });

      const fileSuccess = await this.fileManager.fileService.uploadFiles({
        metadata: { admin: '1' },
      });

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${key}`,
            entity_id: this.selectedPatientGroup.value,
            entity_type: EntityType.DRIVER,
            type: EventType.PATIENT_DRIVER_TEMPLATE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success();
        } else {
          void this.apiService.removeFile(this.getFilePath(file.bucket_key));
          this.overlayService.error(errors);
        }

        this.ref.close(true);
      }
    }

    this.loading.set(false);
  }

  pathFn: () => string = () => '';
}
